import { Download } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  Tooltip
} from '@mui/material'
import { doc, getDoc } from 'firebase/firestore'
import { useEffect, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import { PaperComponent } from '../Helpers/Drag'

const DownloadButton = ({
  document,
  icon = true,
  db,
  isPulse = false,
  isMenuItem = false
}) => {
  const [transactionData, setTransactionData] = useState([])
  const [open, setOpen] = useState(false)
  const [userData, setUserData] = useState(isPulse)
  const csvLink = useRef()

  function getDateString (date) {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' }
    return new Date(date).toLocaleDateString(undefined, options)
  }

  const escapeCsvValue = value => {
    if (
      typeof value === 'string' &&
      (value.includes(',') || value.includes('\n'))
    ) {
      return `${value.replaceAll(',', ';').replaceAll('\n', ' ')}`
    }
    return value
  }

  const arrayToCsv = (array, header) => {
    const csvString = [
      header,
      ...array.map(item => Object.values(item).map(escapeCsvValue))
    ]
    return csvString
  }

  async function getReplies (currentComment) {
    if (db) {
      let ref = doc(db, 'documents', document.id, 'replies', currentComment.id)
      let snap = await getDoc(ref)
      if (snap.exists()) {
        let data = snap.data()
        console.log('Replies for comment', currentComment.id, data.replies)
        return data.replies || []
      }
    }
    return []
  }

  const getSectionType = section => {
    if (section.isABTesting) {
      return 'AB Testing'
    } else if (section.isMultipleChoice) {
      return 'Multiple Choice Grid'
    } else if (section.isChoice) {
      return 'Multiple Choice'
    } else {
      return 'Slider'
    }
  }

  const downloadReport = async () => {
    if (isPulse) {
      if (document.comments) {
        let str = [
          [
            'Report for: ' + document.fileName,
            'Downloaded Report on: ' + getDateString(Date.now())
          ]
        ]

        document.pulseSections.forEach((section, sectionIndex) => {
          let commentsArray = document.comments.filter(
            comment => comment.sectionIndex === sectionIndex
          )
          if (section.isABTesting) {
            let pollAnswers = section.pulseOptions.map(value => {
              return {
                option: value.text,
                votes: commentsArray.reduce((acc, curr) => {
                  return curr.option === value.text ? acc + 1 : acc
                }, 0)
              }
            })

            let percentMap = section.pulseOptions.map(option => {
              return {
                title: section.title,
                type: 'AB Testing',
                option: option.text.replace(/<[^>]*>/g, ''),
                votes:
                  (pollAnswers.find(item => item.option === option.text)
                    .votes || 0) +
                  ' vote' +
                  ((pollAnswers.find(item => item.option === option.text)
                    .votes || 0) === 1
                    ? ''
                    : 's'),
                percent:
                  pollAnswers.reduce((acc, curr) => acc + curr.votes, 0) > 0
                    ? '' +
                      Math.floor(
                        ((pollAnswers.find(item => item.option === option.text)
                          .votes || 0) /
                          pollAnswers.reduce(
                            (acc, curr) => acc + curr.votes,
                            0
                          )) *
                          100
                      ) +
                      '%'
                    : ''
              }
            })
            const headers = ['Title', 'Type', 'Option', 'Votes', 'Percentage']

            str.push(...arrayToCsv(percentMap, headers))
            str.push(['Total Votes: ' + commentsArray.length])
            str.push([])
            str.push([])
            str.push([])
          } else if (section.isChoice) {
            let pollAnswers = section.pulseChoice.map((choice, choiceIndex) => {
              return {
                option: choice,
                votes: commentsArray
                  .map(arr => arr.option)
                  .reduce((acc, curr) => {
                    return curr[choiceIndex].label === choice &&
                      curr[choiceIndex].value
                      ? acc + 1
                      : acc
                  }, 0)
              }
            })
            let totalVotes = commentsArray.length
            let percentMap = section.pulseChoice.map((choice, choiceIndex) => {
              return {
                title: section.title,
                type: 'Multiple Choice',
                option: choice.replace(/<[^>]*>/g, ''),
                votes:
                  pollAnswers[choiceIndex].votes +
                  ' vote' +
                  (pollAnswers[choiceIndex].votes === 1 ? '' : 's'),
                percent:
                  Math.floor(
                    (pollAnswers[choiceIndex].votes /
                      (totalVotes ? totalVotes : 1)) *
                      100
                  ) + '%'
              }
            })
            const headers = ['Title', 'Type', 'Option', 'Votes', 'Percentage']

            str.push(...arrayToCsv(percentMap, headers))
            str.push(['Total Votes: ' + totalVotes])
            str.push([])
            str.push([])
            str.push([])
          } else if (section.isMultipleChoice) {
            let pollAnswers = section.pulseGrid.rows.map((row, rowIndex) => {
              let vals = section.pulseGrid.columns.map(col => {
                return {
                  option: row + ' - ' + col,
                  votes: commentsArray
                    .map(arr => arr.option)
                    .reduce((acc, curr) => {
                      return curr[rowIndex].label === row &&
                        curr[rowIndex].value === col
                        ? acc + 1
                        : acc
                    }, 0)
                }
              })
              return vals
            })

            let percentMap = []
            pollAnswers.forEach((row, rowIndex) => {
              let title = section.pulseGrid.rows[rowIndex].replace(
                /<[^>]*>/g,
                ''
              )
              let type = 'Multiple Choice Grid'
              let rows = row.map((option, colIndex) => {
                let subtitle = section.pulseGrid.columns[colIndex]
                return {
                  title: section.title,
                  type: type,
                  row: title,
                  col: subtitle,
                  votes:
                    option.votes + ' vote' + (option.votes === 1 ? '' : 's'),
                  percent:
                    row.reduce((acc, curr) => acc + curr.votes, 0) > 0
                      ? '' +
                        Math.floor(
                          (option.votes /
                            row.reduce((acc, curr) => acc + curr.votes, 0)) *
                            100
                        ) +
                        '%'
                      : ''
                }
              })
              percentMap = [...percentMap, ...rows, {}]
            })
            const headers = [
              'Title',
              'Type',
              'Row',
              'Column',
              'Votes',
              'Percentage'
            ]

            str.push(...arrayToCsv(percentMap, headers))
            str.push(['Total Votes: ' + commentsArray.length])
            str.push([])
            str.push([])
            str.push([])
          } else {
            str.push(['Section - ' + section.title])
            section.pulsePrompts.forEach((prompt, pulseIndex) => {
              commentsArray = document.comments.filter(
                comment =>
                  comment.sectionIndex === sectionIndex &&
                  comment.pulseIndex === pulseIndex
              )
              let pollAnswers = document.pulseValues.map(value => {
                return {
                  option: value,
                  votes: commentsArray.reduce((acc, curr) => {
                    return curr.option === value ? acc + 1 : acc
                  }, 0)
                }
              })
              let percentMap = pollAnswers.map(answer => {
                return {
                  title: prompt,
                  type: 'Slider',
                  option: answer.option,
                  votes:
                    answer.votes + ' vote' + (answer.votes === 1 ? '' : 's'),
                  percent:
                    pollAnswers.reduce((acc, curr) => acc + curr.votes, 0) > 0
                      ? Math.floor(
                          (answer.votes /
                            pollAnswers.reduce(
                              (acc, curr) => acc + curr.votes,
                              0
                            )) *
                            100
                        ) + '%'
                      : ''
                }
              })
              const headers = ['Title', 'Type', 'Option', 'Votes', 'Percentage']

              str.push(...arrayToCsv(percentMap, headers))
              str.push(['Total Votes: ' + commentsArray.length])
              str.push([])
            })
            str.push([])
            str.push([])
          }
        })

        if (userData) {
          let comments = document.comments
            .sort((a, b) => {
              if (a.sectionIndex === b.sectionIndex) {
                return a.pulseIndex - b.pulseIndex
              }
              return a.sectionIndex - b.sectionIndex
            })
            .map(comment => {
              let prompt
              let option
              if (document.pulseSections[comment.sectionIndex].isABTesting) {
                prompt = ''
                option = comment.option.replace(/<[^>]*>/g, '')
              } else if (
                document.pulseSections[comment.sectionIndex].isMultipleChoice
              ) {
                prompt = comment.option
                  .map(
                    (val, index) =>
                      index +
                      1 +
                      '. ' +
                      val.label.replace(/<[^>]*>/g, '') +
                      '; '
                  )
                  .join('')
                option = comment.option
                  .map((val, index) => index + 1 + '. ' + val.value + '; ')
                  .join('')
              } else if (
                document.pulseSections[comment.sectionIndex].isChoice
              ) {
                prompt = comment.option
                  .map((val, index) =>
                    val.value
                      ? index +
                        1 +
                        '. ' +
                        val.label.replace(/<[^>]*>/g, '') +
                        '; '
                      : ''
                  )
                  .join('')
                option = comment.option
                  .map((val, index) => (val.value ? index + 1 + '. ' : ''))
                  .join('')
              } else {
                prompt =
                  document.pulseSections[comment.sectionIndex].pulsePrompts[
                    comment.pulseIndex
                  ]
                option = comment.option
              }
              return {
                User: comment.displayName ?? comment.uid,
                Header:
                  document.pulseSections[comment.sectionIndex].header?.title ||
                  '',
                Section: document.pulseSections[comment.sectionIndex].title,
                SectionType: getSectionType(
                  document.pulseSections[comment.sectionIndex]
                ),
                Prompt: prompt,
                Option: option,
                Comment: comment.message,
                Date: comment.dateString,
                Type: comment.messageType
              }
            })
          str.push(
            ...arrayToCsv(comments, [
              'User',
              'Header',
              'Section',
              'Section Type',
              'Prompt',
              'Option',
              'Comment',
              'Date',
              'Type'
            ])
          )
        }
        str.push([])
        str.push([
          'Comment Count:',
          document.comments ? document.comments.length : 0
        ])
        str.push([])
        str.push(['View Count', document.visited])
        str.push([
          'Distinct Viewers',
          document.visitedBy ? document.visitedBy.length : 0
        ])
        str.push([])
        if (userData) {
          str.push(['Viewer Name', 'Viewer Email'])
          str.push([])
          document.visitedBy &&
            document.visitedBy.forEach(viewer => {
              str.push([viewer.displayName, viewer.email])
            })
        }
        setTransactionData(str)
      }
      return
    }

    if (document.comments) {
      const commentPromises = document.comments.map(async comment => {
        const itemRows = [
          {
            User: comment.displayName ?? comment.uid,
            Comment: comment.message,
            Date: comment.dateString,
            Type: comment.messageType,
            Page: comment.pageNumber
          }
        ]

        let replies = await getReplies(comment)
        replies.forEach(reply => {
          itemRows.push({
            User: reply.displayName ?? reply.uid ?? comment.displayName ?? comment.uid,
            Comment:
              reply.messageType === 'Like'
                ? "Liked '" +
                  (comment.message ? comment.message : 'A LIKE') +
                  "'"
                : "Reply to '" +
                  (comment.message ? comment.message.substring(0, 10) : '') +
                  "': " +
                  reply.message,
            Date: reply.dateString,
            Type: 'Comment ' + reply.messageType,
            Page: comment.pageNumber
          })
        })

        return itemRows
      })

      const nestedComments = await Promise.all(commentPromises)
      const comments = nestedComments.flat()

      let str = arrayToCsv(comments, [
        'User',
        'Comment',
        'Date',
        'Type',
        'Page'
      ])
      str.push([])
      str.push([
        'Comment Count:',
        document.comments ? document.comments.length : 0
      ])
      str.push([])
      str.push(['View Count', document.visited])
      str.push([
        'Distinct Viewers',
        document.visitedBy ? document.visitedBy.length : 0
      ])
      str.push([])
      str.push(['Viewer Name', 'Viewer Email'])
      str.push([])
      document.visitedBy &&
        document.visitedBy.forEach(viewer => {
          str.push([viewer.displayName, viewer.email])
        })
      setTransactionData(str)
    }
  }

  useEffect(() => {
    if (transactionData.length > 0) {
      csvLink.current.link.click()
      setTransactionData([])
    }
  }, [transactionData])

  return (
    <>
      {isMenuItem ? (
        <>
          <MenuItem
            onClick={() => {
              isPulse ? setOpen(true) : downloadReport()
            }}
          >
            <Download sx={{ mr: 1 }} />
            Download Report
          </MenuItem>
        </>
      ) : (
        <>
          {icon ? (
            <Tooltip title='Download Report'>
              <IconButton
                onClick={() => (isPulse ? setOpen(true) : downloadReport())}
              >
                <Download />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Download Report'>
              <Button
                onClick={() => (isPulse ? setOpen(true) : downloadReport())}
                sx={{ minWidth: 'unset' }}
              >
                <Download />
              </Button>
            </Tooltip>
          )}
        </>
      )}
      <CSVLink
        data={transactionData}
        filename={'Community Document Report for:' + document.fileName + '.csv'}
        style={{ display: 'none' }}
        ref={csvLink}
        target='_blank'
        rel='noreferrer'
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        PaperComponent={PaperComponent}
        aria-labelledby='draggable-dialog-title'
      >
        <DialogTitle style={{ cursor: 'move' }}>
          Download Report for a Pulse
        </DialogTitle>
        <DialogContent>
          <Switch
            checked={userData}
            onChange={() => setUserData(!userData)}
            slotProps={{ input: { 'aria-label': 'controlled' } }}
          />
          {userData ? 'Download User Responses' : 'Download Overall Data'}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setOpen(false)
              downloadReport()
            }}
            variant='contained'
            startIcon={<Download />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DownloadButton